import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { RichTextViewer as QuillRichTextViewer, Card, Column, Typography } from 'seagull';
import '~/components/AgreementStudioPage/TemplateVariables/TemplateVariableBlot/TemplateVariableBlot';
import { Calendar, DollarCircle, Invoice, InvoiceEdit, Note, UserCheck } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOptionalServicesSelection } from '~/client-flow/clientOb/ClientOnboardingInnerV2/hooks/useOptionalServicesSelection';
import { usePackageSelection } from '~/client-flow/clientOb/ClientOnboardingInnerV2/hooks/usePackageSelection';
import { useCobConfig } from '~/client-flow/clientOb/CobConfigContext';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import styles from './AdditionalTerms.module.scss';
import { additionalTermsTextsPath, getAdditionalTermsData, getServiceTermsEntries } from './additionalTerms.utils';
import { TermItem } from './TermItem';
export const GetAdditionalTermsTID = ()=>getTestIds();
const iconMap = {
    effectiveDate: /*#__PURE__*/ _jsx(Calendar, {}),
    paymentTerms: /*#__PURE__*/ _jsx(DollarCircle, {}),
    amendments: /*#__PURE__*/ _jsx(InvoiceEdit, {}),
    outOfScopeCharges: /*#__PURE__*/ _jsx(Invoice, {}),
    accountantAccess: /*#__PURE__*/ _jsx(UserCheck, {}),
    agreementNotes: /*#__PURE__*/ _jsx(Note, {})
};
const testIdMap = {
    effectiveDate: ()=>GetAdditionalTermsTID().AdditionalTermsTestIds.EffectiveDate(),
    paymentTerms: ()=>GetAdditionalTermsTID().AdditionalTermsTestIds.PaymentTerms(),
    amendments: ()=>GetAdditionalTermsTID().AdditionalTermsTestIds.Amendments(),
    outOfScopeCharges: ()=>GetAdditionalTermsTID().AdditionalTermsTestIds.OutOfScopeCharges(),
    accountantAccess: ()=>GetAdditionalTermsTID().AdditionalTermsTestIds.AccountantAccess(),
    agreementNotes: ()=>GetAdditionalTermsTID().AdditionalTermsTestIds.AgreementNotes()
};
export const AdditionalTerms = ()=>{
    const { relationship } = useCobConfig();
    const { selectedBundleId } = usePackageSelection();
    const { selectedOptionalServices } = useOptionalServicesSelection();
    const { SERVICE_LEVEL_TERMS } = useFeatureFlags();
    const { AdditionalTermsTestIds } = useMemo(()=>GetAdditionalTermsTID(), []);
    const { t: additionalTermsTexts } = useLocaleKeys(additionalTermsTextsPath);
    const termItems = useMemo(()=>getAdditionalTermsData(relationship, additionalTermsTexts).map((item)=>({
                icon: iconMap[item.key],
                title: item.title,
                description: item.key === 'agreementNotes' ? /*#__PURE__*/ _jsx(QuillRichTextViewer, {
                    testId: "extra-clause-rich-text-viewer",
                    value: item.description
                }) : item.description,
                testId: testIdMap[item.key]()
            })), [
        relationship,
        additionalTermsTexts
    ]);
    const serviceTermsEntries = useMemo(()=>SERVICE_LEVEL_TERMS ? getServiceTermsEntries({
            relationship,
            selectedBundleId,
            selectedOptionalServiceIds: selectedOptionalServices
        }) : [], [
        relationship,
        SERVICE_LEVEL_TERMS,
        selectedBundleId,
        selectedOptionalServices
    ]);
    return /*#__PURE__*/ _jsxs(Card, {
        bi: {
            name: 'additional_terms_card'
        },
        className: styles.card,
        testId: AdditionalTermsTestIds.Card(),
        children: [
            /*#__PURE__*/ _jsx(Column, {
                gap: 20,
                children: termItems.map((item, idx)=>/*#__PURE__*/ _jsx(TermItem, {
                        icon: item.icon,
                        title: item.title,
                        description: item.description,
                        testId: item.testId
                    }, idx))
            }),
            serviceTermsEntries.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: styles.serviceTermsSection,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        weight: "normal",
                        children: additionalTermsTexts.serviceTerms()
                    }),
                    serviceTermsEntries.map((entry)=>/*#__PURE__*/ _jsxs("div", {
                            className: styles.serviceTermsEntry,
                            "data-testid": AdditionalTermsTestIds.ServiceTerms(entry.serviceId),
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "bold",
                                    children: entry.title
                                }),
                                /*#__PURE__*/ _jsx(QuillRichTextViewer, {
                                    testId: AdditionalTermsTestIds.ServiceTermsViewer(entry.serviceId),
                                    value: entry.description,
                                    skin: "secondary"
                                })
                            ]
                        }, entry.serviceId))
                ]
            })
        ]
    });
};
